import React, { forwardRef, useImperativeHandle } from 'react';
import { FeeComponentModel } from '../../types/client';
import styles from './FeeStructure.module.css';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFeeStructure } from './hooks/useFeeStructure';
import { FeeStructureTable } from './FeeStructureComponents';
import { formatPercentage } from '@/utils/formatters';
import { buildHurdleSteps, getBenchmarkLabel } from './utils/hurdleFormat';
import commonStyles from './ClientCommon.module.css';
import { Button, Note, Pill, Placeholder } from '@/components/ui';

const FALLBACK_DURATION_CHOICES = [
  { value: '3yearly', label: '3 Yearly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

interface FeeStructureProps {
  isAIF?: boolean;
  client_id?: number;
  client_code: string;
  scheme_id: number;
  viewMode?: boolean;
  onModified?: () => void;
  previousFeeComponents?: FeeComponentModel[];
  data?: FeeComponentModel[];
}

export interface FeeStructureRef {
  getPendingChanges: () => FeeComponentModel | null;
  getDeletedStructures: () => number[] | null;
}

const FeeStructure = forwardRef<FeeStructureRef, FeeStructureProps>(({
  client_id,
  client_code,
  scheme_id,
  viewMode = false,
  onModified,
  previousFeeComponents,
  data
}, ref) => {
  const {
    mode,
    feeComponentsList,
    listLoading,
    loading,
    actionLoadingId,
    feeComponent,
    validationErrors,
    error,
    isViewingHistory,
    deletedStructures,
    choices,
    handleCreateNew,
    handleViewDetails,
    handleEdit,
    handleDelete,
    handleBackToList,
    handleInputChange,
    handleStructureChange,
    addStructure,
    removeStructure,
    validate,
    handleSave,
  } = useFeeStructure({
    clientId: client_id,
    clientCode: client_code,
    schemeId: scheme_id,
    viewMode,
    onModified,
    data,
    previousFeeComponents
  });

  useImperativeHandle(ref, () => ({
    getPendingChanges: () => {
      if (mode === 'form' && !isViewingHistory && validate()) {
        return feeComponent;
      }
      return null;
    },
    getDeletedStructures: () => deletedStructures
  }));

  const isEditable = !viewMode && !isViewingHistory;
  const durationOptions = choices.duration_choices.length ? choices.duration_choices : FALLBACK_DURATION_CHOICES;

  // --- Helper Functions for List View ---

  const renderHurdleTooltip = (comp: FeeComponentModel) => {
    return (
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'transparent', color: 'inherit' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, borderBottom: '1px solid var(--nav-line)', pb: 0.5, fontWeight: 700 }}>
          Performance Fee Steps
        </Typography>
        <Stack spacing={1}>
          {comp.fee_structures?.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--navy-100)' }}>
                Slab {s.step_no}
              </Typography>
              <Typography variant="caption">
                {s.hurdle_type === 'Benchmark'
                  ? `Benchmark: ${getBenchmarkLabel(s.hurdle_benchmark)}`
                  : `Hurdle Value: ${formatPercentage(s.hurdle_value)}`}
                {' '}→ Fee: {formatPercentage(s.fee_value)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  };

  // Check if we're in approval/comparison mode (previousFeeComponents explicitly provided)
  const isApprovalComparisonMode = previousFeeComponents !== undefined;

  // Numeric fields that should be compared as numbers (handles "1.20" vs "1.2" cases)
  const numericFields = ['management_fee', 'distributor_manage_fee_share', 'distributor_performance_fee_share', 'exit_load'];

  const areValuesEqual = (fieldName: string, val1: any, val2: any): boolean => {
    // Handle null/undefined/empty cases
    const isEmpty1 = val1 === null || val1 === undefined || val1 === '';
    const isEmpty2 = val2 === null || val2 === undefined || val2 === '';
    if (isEmpty1 && isEmpty2) return true;
    if (isEmpty1 || isEmpty2) return false;

    // For numeric fields, compare as numbers; text like an exit load of "3-2-1" is NaN,
    // and NaN never equals itself, so it falls through to the plain comparison.
    if (numericFields.includes(fieldName) && !isNaN(Number(val1)) && !isNaN(Number(val2))) {
      return Number(val1) === Number(val2);
    }

    // For other fields, use strict equality
    return val1 === val2;
  };

  const getPreviousComponent = (compId: number | undefined, index?: number) => {
    if (!isApprovalComparisonMode || !previousFeeComponents || previousFeeComponents.length === 0) return null;

    // First try to match by ID
    if (compId) {
      const matchById = previousFeeComponents.find(p => p.id === compId);
      if (matchById) return matchById;
    }

    // Fallback: match by index (useful when IDs differ in approval data)
    if (index !== undefined && previousFeeComponents[index]) {
      return previousFeeComponents[index];
    }

    // Last fallback: if only one component in both, compare them
    if (previousFeeComponents.length === 1 && feeComponentsList.length === 1) {
      return previousFeeComponents[0];
    }

    return null;
  };

  const isComponentChanged = (comp: FeeComponentModel, index?: number) => {
    // Only check for changes in approval mode
    if (!isApprovalComparisonMode) return false;

    const prev = getPreviousComponent(comp.id, index);

    // No previous component found - this is a NEW component
    if (!prev) {
      return true;
    }

    // Check main fields for changes using proper comparison
    return (
      !areValuesEqual('valid_from', comp.valid_from, prev.valid_from) ||
      !areValuesEqual('valid_to', comp.valid_to, prev.valid_to) ||
      !areValuesEqual('management_fee', comp.management_fee, prev.management_fee) ||
      !areValuesEqual('management_fee_calc_duration', comp.management_fee_calc_duration, prev.management_fee_calc_duration) ||
      !areValuesEqual('performance_fee_calc_duration', comp.performance_fee_calc_duration, prev.performance_fee_calc_duration) ||
      !areValuesEqual('distributor_manage_fee_share', comp.distributor_manage_fee_share, prev.distributor_manage_fee_share) ||
      !areValuesEqual('distributor_performance_fee_share', comp.distributor_performance_fee_share, prev.distributor_performance_fee_share) ||
      !areValuesEqual('exit_load', comp.exit_load, prev.exit_load) ||
      !areValuesEqual('is_active', comp.is_active, prev.is_active)
    );
  };

  const renderPreviousValue = (fieldName: keyof FeeComponentModel, index?: number) => {
    // Only show previous values in approval comparison mode
    if (!isApprovalComparisonMode || !feeComponent) return null;

    const prev = getPreviousComponent(feeComponent.id, index);

    // If no previous component, this is a new fee component
    if (!prev) {
      if (previousFeeComponents && previousFeeComponents.length === 0) {
        return <span className={commonStyles['previous-value']}>Previously: N/A (New)</span>;
      }
      return null;
    }

    const previousValue = (prev as any)[fieldName];
    const currentValue = (feeComponent as any)[fieldName];

    // Use proper comparison for numeric fields
    if (areValuesEqual(fieldName, previousValue, currentValue)) return null;

    let displayValue = '';
    if (previousValue === null || previousValue === undefined || previousValue === '') {
      displayValue = 'EMPTY';
    } else if (typeof previousValue === 'boolean') {
      displayValue = previousValue ? 'YES' : 'NO';
    } else if (fieldName === 'management_fee' || fieldName.includes('share')) {
      displayValue = isNaN(Number(previousValue)) ? `${previousValue}%` : formatPercentage(previousValue);
    } else if (fieldName.includes('duration')) {
      displayValue = durationOptions.find(c => c.value === previousValue)?.label || String(previousValue).toUpperCase();
    } else {
      displayValue = String(previousValue);
    }

    return <span className={commonStyles['previous-value']}>Previously: {displayValue}</span>;
  };

  if (listLoading) {
    return (
      <Placeholder kind="loading" />
    );
  }

  if (mode === 'list') {
    return (
      <Box sx={{ width: '100%', p: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--text)' }}>
            Fee Structures History
          </Typography>
          {!viewMode && (
            <Button
              icon={<AddIcon fontSize="small" />}
              variant="primary"
              size="sm"
              onClick={handleCreateNew}
            >
              New Version
            </Button>
          )}
        </Stack>

        {error && <Box sx={{ mb: 2 }}><Note tone="error">{error}</Note></Box>}

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 2 }}>
          <Table
            size="small"
            sx={{
              '& th, & td': {
                borderBottom: '1px solid var(--border)',
              },
            }}
          >
            {/* ===================== HEADER ===================== */}
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
                <TableCell rowSpan={2} sx={{ fontWeight: 600 }}>
                  Valid From
                </TableCell>
                <TableCell rowSpan={2} sx={{ fontWeight: 600 }}>
                  Valid To
                </TableCell>
                <TableCell rowSpan={2} sx={{ fontWeight: 600 }} align="right">
                  Mgmt Fee (%)
                </TableCell>
                <TableCell rowSpan={2} sx={{ fontWeight: 600 }}>
                  Performance Fee Hurdle
                </TableCell>

                <TableCell
                  colSpan={2}
                  align="left"
                  sx={{
                    fontWeight: 600,
                    borderLeft: '1px solid var(--border)',
                  }}
                >
                  Fee Duration
                </TableCell>

                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'var(--accent-soft)',
                    borderLeft: '1px solid var(--border)',
                  }}
                >
                  Distributor Share
                </TableCell>

                <TableCell
                  rowSpan={2}
                  align="right"
                  sx={{
                    fontWeight: 600,
                    borderLeft: '1px solid var(--border)',
                  }}
                >
                  Exit Load
                </TableCell>

                <TableCell rowSpan={2} align="left" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>

              <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
                <TableCell align="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Mgmt
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Perf
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    borderLeft: '1px solid var(--border)',
                  }}
                >
                  Mgmt %
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Perf %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeComponentsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No fee structures found.
                  </TableCell>
                </TableRow>
              ) : (
                feeComponentsList.map((comp, idx) => {
                  const changed = isComponentChanged(comp, idx);
                  const hurdleSteps = buildHurdleSteps(comp.fee_structures);
                  return (
                    <TableRow
                      key={comp.id ?? idx}
                      hover
                      sx={{
                        ...(changed && {
                          bgcolor: 'var(--warn-soft)',
                        }),
                      }}
                    >
                      <TableCell>{comp.valid_from}</TableCell>
                      <TableCell align="left">{comp.valid_to || '—'}</TableCell>

                      <TableCell align="right">
                        {formatPercentage(comp.management_fee)}
                      </TableCell>

                      <TableCell align="left">
                        <Tooltip
                          title={renderHurdleTooltip(comp)}
                          arrow
                          placement="top"
                        >
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25, cursor: 'help' }}>
                            {hurdleSteps.length === 0 ? (
                              '—'
                            ) : (
                              hurdleSteps.map((hurdleStep, stepIdx) => (
                                <Box key={stepIdx} sx={{ whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                                  {hurdleStep.hurdle}
                                  <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>→</Box>
                                  {hurdleStep.fee}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ textTransform: 'capitalize' }} align="left">
                        {comp.management_fee_calc_duration_display || comp.management_fee_calc_duration}
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }} align="left">
                        {comp.performance_fee_calc_duration_display || comp.performance_fee_calc_duration}
                      </TableCell>

                      <TableCell align="right">
                        {formatPercentage(comp.distributor_manage_fee_share)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPercentage(comp.distributor_performance_fee_share)}
                      </TableCell>

                      <TableCell align="right">{comp.exit_load || '—'}</TableCell>

                      <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                          <Tooltip title="View Details">
                            <Button iconOnly aria-label="View Details" onClick={() => handleViewDetails(comp)}>
                              <VisibilityIcon fontSize="small" />
                            </Button>
                          </Tooltip>

                          {!viewMode && (
                            <>
                              <Tooltip title="Edit">
                                <Button iconOnly aria-label="Edit" onClick={() => handleEdit(comp)}>
                                  <EditIcon fontSize="small" />
                                </Button>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <Button
                                  iconOnly
                                  aria-label="Delete"
                                  disabled={actionLoadingId === comp.id}
                                  onClick={() => handleDelete(comp)}
                                  style={{ color: 'var(--neg)' }}
                                >
                                  {actionLoadingId === comp.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!feeComponent) return null;

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button iconOnly aria-label="Back to list" onClick={handleBackToList}>
          <ArrowBackIcon fontSize="small" />
        </Button>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--text)' }}>
          {isViewingHistory ? 'Fee Structure Details' : feeComponent.id ? 'Update Fee Structure' : 'New Fee Structure'}
        </Typography>
        {isViewingHistory && isComponentChanged(feeComponent) && (
          <Pill tone="warning">Modified</Pill>
        )}
        {!isEditable && <Pill tone="neutral" plain>Read Only</Pill>}
      </Stack>

      <form>
        <Stack spacing={3}>
          <Card elevation={0} className={styles['fee-card']}>
            <CardContent sx={{ p: 3 }}>
              {/* Validity Section */}
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
                VALIDITY & STATUS
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <TextField
                    label="Valid From"
                    type="date"
                    value={feeComponent.valid_from || ''}
                    onChange={(e) => handleInputChange('valid_from', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={!!validationErrors.valid_from}
                    helperText={validationErrors.valid_from}
                  />
                  {renderPreviousValue('valid_from')}
                </Box>
                <Box>
                  <TextField
                    label="Valid To"
                    type="date"
                    value={feeComponent.valid_to || ''}
                    onChange={(e) => handleInputChange('valid_to', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    InputLabelProps={{ shrink: true }}
                  />
                  {renderPreviousValue('valid_to')}
                </Box>
                <Box display="flex" flexDirection="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!feeComponent.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        disabled={!isEditable}
                      />
                    }
                    label="Is Active"
                  />
                  {renderPreviousValue('is_active')}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Fee Configuration Section */}
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
                FEE CALCULATION SETTINGS
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 1 }}>
                <Box>
                  <TextField
                    label="Management Fee (%)"
                    type="number"
                    value={feeComponent.management_fee ?? ''}
                    onChange={(e) => handleInputChange('management_fee', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                    error={!!validationErrors.management_fee}
                    helperText={validationErrors.management_fee}
                  />
                  {renderPreviousValue('management_fee')}
                </Box>
                <Box>
                  <TextField
                    select
                    label="Mgmt. Fee Duration"
                    value={feeComponent.management_fee_calc_duration || 'yearly'}
                    onChange={(e) => handleInputChange('management_fee_calc_duration', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                  >
                    <MenuItem value="yearly" disabled>Select Duration</MenuItem>
                    {durationOptions.map(choice => (
                      <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                    ))}
                  </TextField>
                  {renderPreviousValue('management_fee_calc_duration')}
                </Box>
                <Box>
                  <TextField
                    select
                    label="Perf. Fee Duration"
                    value={feeComponent.performance_fee_calc_duration || 'yearly'}
                    onChange={(e) => handleInputChange('performance_fee_calc_duration', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                  >
                    <MenuItem value="yearly" disabled>Select Duration</MenuItem>
                    {durationOptions.map(choice => (
                      <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                    ))}
                  </TextField>
                  {renderPreviousValue('performance_fee_calc_duration')}
                </Box>
                <Box>
                  <TextField
                    label="Exit Load"
                    value={feeComponent.exit_load ?? ''}
                    onChange={(e) => handleInputChange('exit_load', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                  />
                  {renderPreviousValue('exit_load')}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Distributor Section */}
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
                DISTRIBUTOR SHARES
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    label="Distributor Mgmt Share (%)"
                    type="number"
                    value={feeComponent.distributor_manage_fee_share ?? ''}
                    onChange={(e) => handleInputChange('distributor_manage_fee_share', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                    error={!!validationErrors.distributor_manage_fee_share}
                    helperText={validationErrors.distributor_manage_fee_share}
                  />
                  {renderPreviousValue('distributor_manage_fee_share')}
                </Box>
                <Box>
                  <TextField
                    label="Distributor Perf. Share (%)"
                    type="number"
                    value={feeComponent.distributor_performance_fee_share ?? ''}
                    onChange={(e) => handleInputChange('distributor_performance_fee_share', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    fullWidth
                    size="small"
                    disabled={!isEditable}
                    variant="outlined"
                    error={!!validationErrors.distributor_performance_fee_share}
                    helperText={validationErrors.distributor_performance_fee_share}
                  />
                  {renderPreviousValue('distributor_performance_fee_share')}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Performance Fee Table Card */}
          <Card elevation={0} className={styles['fee-card']}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>
                    PERFORMANCE FEE HURDLES
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Configure hurdle benchmarks and corresponding fee percentages.
                  </Typography>
                </Box>
                {isEditable && (
                  <Button
                    onClick={addStructure}
                    icon={<AddIcon fontSize="small" />}
                    size="sm"
                  >
                    Add Step
                  </Button>
                )}
              </Stack>

              {validationErrors.fee_structures_general && (
                <Box sx={{ mb: 2 }}><Note tone="error">{validationErrors.fee_structures_general}</Note></Box>
              )}

              <FeeStructureTable
                structures={feeComponent.fee_structures || []}
                onStructureChange={handleStructureChange}
                onRemoveStructure={removeStructure}
                isEditMode={isEditable}
                errors={validationErrors.fee_structures || []}
                hurdleTypeChoices={choices.hurdle_type_choices}
                hurdleBenchmarkChoices={choices.hurdle_benchmark_choices}
              />
            </CardContent>
          </Card>

          {!viewMode && !isViewingHistory && !client_id && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>
                Changes will be saved when you submit the client form.
              </Typography>
            </Box>
          )}

          {!viewMode && !isViewingHistory && client_id && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
              <Button
                onClick={handleBackToList}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={loading}
                style={{ paddingLeft: 32, paddingRight: 32 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : (feeComponent.id ? 'Update Component' : 'Save Component')}
              </Button>
            </Box>
          )}
        </Stack>
      </form>
    </Box>
  );
});

FeeStructure.displayName = 'FeeStructure';

export default FeeStructure;